import { io, Socket } from 'socket.io-client';
import { store } from '../store';
import { setConnectionStatus } from '../store/slices/connectionSlice';

class SocketService {
  private socket: Socket | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 10;
  private reconnectDelay = 1000;

  connect(authData?: { token: string; authType?: 'azure' | 'legacy' }): Promise<boolean> {
    return new Promise((resolve) => {
      if (this.socket?.connected) {
        resolve(true);
        return;
      }

      // Determine the socket URL - if VITE_WEBSOCKET_URL is set, use it directly
      // Otherwise, use the current protocol/host
      let socketUrl = import.meta.env.VITE_WEBSOCKET_URL || 
        (window.location.hostname === 'localhost' 
          ? 'ws://localhost:3002' 
          : `${window.location.protocol}//${window.location.host}`);
      
      // Socket.IO options
      const socketOptions: any = {
        auth: authData ? {
          token: authData.token,
          authType: authData.authType || 'legacy'
        } : undefined,
        transports: ['websocket', 'polling'],
        reconnection: true,
        reconnectionAttempts: this.maxReconnectAttempts,
        reconnectionDelay: this.reconnectDelay,
        reconnectionDelayMax: 10000
      };
      
      // If not connecting directly to port 3002, we're going through nginx
      // Socket.IO client needs the path to be specified
      if (!socketUrl.includes(':3002')) {
        socketOptions.path = '/socket.io/';
        // Also ensure we don't include /socket.io in the URL itself
        socketUrl = socketUrl.replace(/\/socket\.io\/?$/, '');
      }
      
      console.log('Connecting to WebSocket at:', socketUrl, 'with path:', socketOptions.path);

      this.socket = io(socketUrl, socketOptions);

      // Add more debugging
      this.socket.on('error', (error) => {
        console.error('Socket error:', error);
      });

      // Set up one-time connection handler for this promise
      this.socket.once('connect', () => {
        console.log('Connected to WebSocket server');
        resolve(true);
      });

      this.socket.once('connect_error', (error: any) => {
        console.error('Connection failed:', error);
        console.error('Error type:', error.type);
        console.error('Error message:', error.message);
        console.error('Full error:', JSON.stringify(error, null, 2));
        resolve(false);
      });

      this.setupEventListeners();
    });
  }

  private setupEventListeners() {
    if (!this.socket) return;

    this.socket.on('connect', () => {
      console.log('Connected to WebSocket server');
      store.dispatch(setConnectionStatus('connected'));
      this.reconnectAttempts = 0;

      // Notify service worker
      if ('serviceWorker' in navigator && navigator.serviceWorker.controller && this.socket) {
        const token = typeof this.socket.auth === 'object' && this.socket.auth ? 
          (this.socket.auth as any).token : undefined;
        
        navigator.serviceWorker.controller.postMessage({
          type: 'WEBSOCKET_CONNECT',
          url: this.socket.io.opts.hostname || 'localhost',
          token: token
        });
      }
    });

    this.socket.on('disconnect', (reason) => {
      console.log('Disconnected from WebSocket server:', reason);
      store.dispatch(setConnectionStatus('disconnected'));
    });

    this.socket.on('connect_error', (error) => {
      console.error('Connection error:', error);
      this.reconnectAttempts++;
      
      if (this.reconnectAttempts >= this.maxReconnectAttempts) {
        store.dispatch(setConnectionStatus('error'));
      } else {
        store.dispatch(setConnectionStatus('reconnecting'));
      }
    });

    this.socket.on('error', (error) => {
      console.error('Socket error:', error);
    });

    // Authentication response
    this.socket.on('authResponse', (response) => {
      if (!response.success) {
        console.error('Authentication failed:', response.error);
        this.disconnect();
      }
    });

    // Session expired
    this.socket.on('sessionExpired', () => {
      // Handle session expiration
      store.dispatch({ type: 'auth/logout' });
      window.location.href = '/login';
    });
  }

  emit(event: string, data: any, callback?: (response: any) => void) {
    if (!this.socket?.connected) {
      console.error('Socket not connected');
      callback?.({ success: false, error: 'Not connected' });
      return;
    }

    if (callback) {
      this.socket.emit(event, data, callback);
    } else {
      this.socket.emit(event, data);
    }
  }

  on(event: string, handler: (data: any) => void) {
    if (!this.socket) {
      console.error('Socket not initialized');
      return;
    }

    this.socket.on(event, handler);
  }

  off(event: string, handler?: (data: any) => void) {
    if (!this.socket) return;

    if (handler) {
      this.socket.off(event, handler);
    } else {
      this.socket.off(event);
    }
  }

  disconnect() {
    if (this.socket) {
      // Notify service worker
      if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage({
          type: 'WEBSOCKET_DISCONNECT'
        });
      }

      this.socket.disconnect();
      this.socket = null;
    }
  }

  isConnected(): boolean {
    return this.socket?.connected || false;
  }

  getSocket(): Socket | null {
    return this.socket;
  }
}

export const socketService = new SocketService();